import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Modal } from 'antd';
import { ArrowRightOutlined, LoadingOutlined, ShoppingOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { calculateOrderSummary, formatCurrency } from '../../utils';
import { deleteOrder } from '../../redux/actions/order';
import { cancelTerminalPayment } from '../../redux/actions/payment';
import { getRoutes } from '../../routes/Routes';
import { clearOrderBroadcast } from '../../hooks/useOrderBroadcast';
import { decodeId } from '../../utils/idEncoder';

const KioskTimeoutModal = ({
  isOpen,
  remainingSeconds,
  warningDuration = 20,
  onContinue,
  onReset,
  cartItems = [],
  orderId: orderIdProp,
  terminalReference,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCancelling, setIsCancelling] = useState(false);
  const isCancellingRef = useRef(false);

  const rawOrderId = new URLSearchParams(location.search).get('orderId');
  const orderId = orderIdProp || (rawOrderId ? decodeId(rawOrderId) : null);
  const isUrgent = remainingSeconds <= 5;
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, Math.min(1, remainingSeconds / warningDuration));
  const strokeDashoffset = circumference * (1 - progress);
  const { grandTotal } = calculateOrderSummary(cartItems);

  const totalItems = cartItems.reduce(
    (acc, item) => acc + (item.quantity === '' ? 1 : Number(item.quantity) || 1),
    0
  );

  useEffect(() => {
    if (!isOpen) {
      isCancellingRef.current = false;
      setIsCancelling(false);
    }
  }, [isOpen]);

  const handleCancelOrder = useCallback(async () => {
    if (isCancellingRef.current) return;
    isCancellingRef.current = true;
    setIsCancelling(true);
    try {
      if (terminalReference) {
        // Void whatever's in flight before the order itself disappears -
        // otherwise a customer who timed out mid-payment leaves an
        // authorized charge with nothing left to reconcile it against.
        await cancelTerminalPayment({ reference: terminalReference });
      }
      if (orderId) {
        await deleteOrder(orderId, { toast: false });
      }
    } finally {
      clearOrderBroadcast();
      setIsCancelling(false);
      isCancellingRef.current = false;
      if (onReset) {
        onReset({ orderAlreadyDeleted: true });
      } else {
        const customer = location.state?.customer;
        if (customer) {
          navigate({ pathname: getRoutes().account_kiosk.route(), search: 'redirectToHome=true' }, { replace: true });
        } else {
          navigate({ pathname: getRoutes().kiosk.route(), search: 'redirectToHome=true' }, { replace: true });
          window.navigation?.reload?.();
        }
      }
    }
  }, [orderId, terminalReference, onReset, navigate, location.state?.customer]);

  useEffect(() => {
    if (isOpen && remainingSeconds <= 0 && !isCancellingRef.current) {
      handleCancelOrder();
    }
  }, [isOpen, remainingSeconds, handleCancelOrder]);

  return (
    <Modal
      open={isOpen}
      footer={null}
      closable={false}
      centered
      className="kiosk-timeout-modal-advanced"
      width={460}
      zIndex={2500}
      styles={{
        body: { padding: 0 },
      }}
    >
      <div className={clsx('kiosk-adv-timeout-card', { urgent: isUrgent })}>
        {/* Ambient Top Glow */}
        <div className="adv-timeout-ambient-glow" />

        {/* Top Tag */}
        <div className="adv-timeout-status-tag">
          <span className="adv-timeout-dot" />
          <span>INACTIVITY ALERT</span>
        </div>

        {/* Central Glowing Countdown Ring */}
        <div className="adv-timeout-timer-center">
          <div className="adv-timer-pulse-layer" />
          <svg width="140" height="140" viewBox="0 0 140 140" className="adv-timer-svg">
            <defs>
              <linearGradient id="advTimerGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F58220" />
                <stop offset="100%" stopColor="#FF9800" />
              </linearGradient>
              <linearGradient id="advTimerUrgentGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#EF4444" />
                <stop offset="100%" stopColor="#F97316" />
              </linearGradient>
            </defs>
            {/* Background track */}
            <circle cx="70" cy="70" r={radius} className="adv-timer-track" />
            {/* Animated Progress */}
            <circle
              cx="70"
              cy="70"
              r={radius}
              className="adv-timer-fill"
              style={{
                strokeDasharray: circumference,
                strokeDashoffset: strokeDashoffset,
                stroke: isUrgent ? 'url(#advTimerUrgentGrad)' : 'url(#advTimerGrad)',
              }}
            />
          </svg>

          {/* Number in center */}
          <div className="adv-timer-inner">
            <span className="adv-timer-number">
              {remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds}
            </span>
          </div>
        </div>

        {/* Heading & Subtitle */}
        <div className="adv-timeout-text">
          <h2 className="adv-timeout-title">Need More Time?</h2>
          <p className="adv-timeout-desc">
            Your session will time out soon.<br />
            Tap <strong>Continue</strong> to keep shopping.
          </p>
        </div>

        {/* Cart items summary badge */}
        {totalItems > 0 && (
          <div className="adv-cart-preview-pill">
            <ShoppingOutlined className="adv-cart-pill-icon" />
            <span className="adv-cart-pill-text">
              <strong>{totalItems} {totalItems === 1 ? 'item' : 'items'}</strong> in cart ({formatCurrency(grandTotal)})
            </span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="adv-timeout-actions">
          <button
            type="button"
            className="adv-timeout-btn-secondary"
            onClick={handleCancelOrder}
            disabled={isCancelling}
          >
            {isCancelling ? (
              <>
                <LoadingOutlined style={{ marginRight: 6 }} />
                <span>Cancelling...</span>
              </>
            ) : (
              <span>Cancel Order</span>
            )}
          </button>

          <button
            type="button"
            className="adv-timeout-btn-primary"
            onClick={onContinue}
            disabled={isCancelling}
          >
            <span>Continue</span>
            <ArrowRightOutlined className="adv-btn-icon" />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default KioskTimeoutModal;
